import { Box, Button, Chip, Link as MuiLink, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCallback } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { format as formatNumber } from 'numerable';
import { useSingularLinkRedirect } from '@presentation/hooks/useSingularLinkRedirect';
import { LinkGeneratorService } from '@domain/services/LinkGeneratorService';
import type { ContentItemWithCollectionsModel } from '@domain/models/ContentItemWithCollectionsModel';
import { buildPuzzleIntro } from '@presentation/seo/templates';
import { PlayInAppButton } from '@presentation/components/PlayInAppButton';
import { JsonLd } from '@presentation/seo/JsonLd';
import { buildBreadcrumb, buildPuzzleGraph } from '@presentation/seo/schema';

import { ImageColumn, InfoColumn, KeywordList, Layout, PuzzleImage, PuzzleVideo, Stats } from './styles';

type PuzzleProps = {
	puzzle: ContentItemWithCollectionsModel;
};

const Puzzle = ({ puzzle }: PuzzleProps) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const { redirectToApp } = useSingularLinkRedirect(LinkGeneratorService.LINK_TYPES.PUZZLE, puzzle.id);

	const onBack = useCallback(() => {
		if (typeof window !== 'undefined' && window.history.length > 1) {
			navigate(-1);
		} else {
			navigate('/');
		}
	}, [navigate]);

	const author = puzzle.author;
	const intro = buildPuzzleIntro(puzzle);
	const altText = `${puzzle.name} jigsaw puzzle${author?.name ? ` by ${author.name}` : ''}`;
	const path = `/puzzle/${puzzle.id}`;

	return (
		<>
			<JsonLd data={buildPuzzleGraph(puzzle, path)} />
			<JsonLd
				data={buildBreadcrumb([
					{ name: 'Home', url: '/' },
					...(author ? [{ name: author.name, url: `/${author.profileLink}` }] : []),
					{ name: puzzle.name },
				])}
			/>
			<Box sx={{ px: { xs: 2, md: 4 }, pt: 2 }}>
				<Button
					onClick={onBack}
					startIcon={<ArrowBackIcon />}
					color='inherit'
					sx={{ textTransform: 'none', fontSize: 14, fontWeight: 500, color: theme.colors.descriptionText }}
				>
					Back
				</Button>
			</Box>
			<Layout>
				<ImageColumn>
					{puzzle.type === 'video' && puzzle.videoPreview ? (
						<PuzzleVideo
							src={puzzle.videoPreview}
							poster={puzzle.imageSrcFull || puzzle.imageSrc}
							width={480}
							height={480}
							autoPlay
							loop
							muted
							playsInline
							controls={false}
							aria-label={altText}
						/>
					) : (
						<PuzzleImage
							src={puzzle.imageSrcFull || puzzle.imageSrc}
							alt={altText}
							width={480}
							height={480}
							decoding='async'
						/>
					)}
				</ImageColumn>
				<InfoColumn>
					<Typography
						component='h1'
						sx={{ fontSize: { xs: 28, md: 40 }, fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.02em' }}
						color={theme.colors.text}
					>
						{puzzle.name}
					</Typography>
					{author && (
						<Typography component='p' sx={{ mt: 1, fontSize: 15, color: theme.colors.descriptionText }}>
							by{' '}
							<MuiLink
								href={`/${author.profileLink}`}
								underline='hover'
								sx={{ fontWeight: 600 }}
								color={theme.colors.text}
							>
								{author.name}
							</MuiLink>
						</Typography>
					)}
					<Typography
						component='h2'
						sx={{ mt: 2.5, fontSize: { xs: 16, md: 17 }, fontWeight: 400, lineHeight: 1.7, maxWidth: '64ch' }}
						color={theme.colors.text}
					>
						{intro}
					</Typography>
					{puzzle.keywords && puzzle.keywords.length > 0 && (
						<KeywordList>
							{puzzle.keywords.map((keyword, index) => (
								<Chip key={index} label={keyword.trim()} size='small' sx={{ fontSize: 13, fontWeight: 500 }} />
							))}
						</KeywordList>
					)}
					<Stats>
						<span>{formatNumber(puzzle.likes ?? 0, '0 a')} likes</span>
						<span>{formatNumber(puzzle.plays ?? 0, '0 a')} plays</span>
					</Stats>
					<PlayInAppButton size='medium' onClick={redirectToApp} />
					{puzzle.collections && puzzle.collections.length > 0 && (
						<Box sx={{ mt: 4 }}>
							<Typography
								component='h2'
								sx={{ fontSize: { xs: 18, md: 20 }, fontWeight: 700, mb: 1.5 }}
								color={theme.colors.text}
							>
								Collections
							</Typography>
							{puzzle.collections.map((collection) => (
								<MuiLink
									key={collection.id}
									href={`/category/${collection.id}`}
									underline='hover'
									sx={{ display: 'block', fontSize: 15, py: 0.25 }}
									color={theme.colors.link}
								>
									{collection.name}
								</MuiLink>
							))}
						</Box>
					)}
				</InfoColumn>
			</Layout>
		</>
	);
};

export default Puzzle;
